import type { IntlService } from 'ember-intl';
import type { TFormulaError } from 'volta/helpers/custom-field-formula-validation';
import type { IFormulaBuilder } from 'volta/models/types/custom-field-definition';

const addError = (intl: IntlService, errors: TFormulaError, index: string, i18nKey: string) => {
  const error = intl.t(`customFieldsDefinitionsPage.formula.errors.${i18nKey}`);

  if (errors[index]?.includes(error)) {
    return errors;
  }

  const ei = errors[index];
  errors[index] = ei ? [...ei, error] : [error];
  return errors;
};

export default function (
  intl: IntlService,
  formula: IFormulaBuilder | undefined,
  id: string | undefined,
  isPristine: boolean = false
) {
  let errors: TFormulaError = {};
  if (isPristine) {
    return false;
  }
  if (!formula?.id) {
    errors = addError(intl, errors, 'root', 'empty');

    return id ? errors[id] : Object.keys(errors).length > 0;
  }

  const checkNode = (node: IFormulaBuilder, parent?: IFormulaBuilder): IFormulaBuilder => {
    const { customFieldId, value, type, name, args } = node;
    const children = parent?.args;

    if (
      children &&
      parent &&
      parent.name !== 'if' &&
      parent.type === 'OPERATOR' &&
      !!parent.resultType
    ) {
      const hasSameResultTypes = children.every(
        (c: IFormulaBuilder) => c.resultType && c.resultType === parent.resultType
      );
      if (!hasSameResultTypes) {
        const index = parent.id;
        if (index) {
          errors = addError(intl, errors, index, 'resultType');
        }
      }
    }

    if (name && (type === 'OPERATOR' || type === 'FUNCTION') && name !== 'now') {
      if (['lower', 'upper'].includes(name) && (!args || args.length === 0)) {
        const index = node.id;
        if (index) {
          errors = addError(intl, errors, index, 'missingValue');
        }
      } else if (
        ['join', 'add', 'multiply', 'subtract', 'equal', 'notEqual'].includes(name) &&
        (!args || args.length < 2)
      ) {
        const index = node.id;
        if (index) {
          errors = addError(intl, errors, index, 'missingValue');
        }
      } else if (name === 'if' && (!args || args.length < 3)) {
        const index = node.id;
        if (index) {
          errors = addError(intl, errors, index, 'missingValue');
        }
      }
    }

    if ((type === 'PROPERTY' && !customFieldId) || (type === 'CONSTANT' && !value)) {
      const index = node.id;
      if (index) {
        errors = addError(intl, errors, index, 'missingValue');
      }
    }

    if (customFieldId && type === 'CONSTANT') {
      const index = node.id;
      if (index) {
        errors = addError(intl, errors, index, 'type');
      }
    }
    return node;
  };

  const formatTree = (currentNode: IFormulaBuilder, parent?: IFormulaBuilder) => {
    const formattedNode = checkNode(currentNode, parent);
    if (!currentNode.args) {
      return formattedNode;
    }

    formattedNode.args = currentNode.args.map((arg: IFormulaBuilder) =>
      formatTree(arg, currentNode)
    );
    return formattedNode;
  };

  formatTree(formula);

  return id ? errors[id] : Object.keys(errors).length > 0;
}
